import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import { ArrowLeft } from "lucide-react";
import { Footer } from "@/components/product/Footer";
import { BeekeeperSection } from "@/components/product/BeekeeperSection";
import { VideoPlayer } from "@/components/product/videoPlayer";
import { MoreProducts } from "@/components/product/MoreProducts";
import { SurveySection } from "@/components/product/SurveySection";
import { HeroSection } from "@/components/product/HeroSection";
import { FeatureGrid } from "@/components/product/FeatureGrid";
import { About } from "@/components/product/About";
import { FAQ } from "@/components/product/FAQ";
import { Product } from '@/types';
import { RecordModel } from 'pocketbase';
import { JoinCommunity } from "@/components/product/JoinCommunity";
import { FeatureCard } from "@/components/product/FeatureCard";
import { ProductLoader } from "@/components/ui/ProductLoader";
import { Credit } from "@/components/product/Credit";
import { Customers } from "@/components/product/Customers";

interface ProductRecord extends RecordModel {
  name: string;
  description: string;
  image: string;
  price: number;
  cooperative: string;
  source?: string;
  shortDescription: string;
  slug: string;
  moreProductsVisibility: boolean;
  customers?: Array<{
    name: string;
    logo: string;
    url?: string;
  }>;
  credit?: {
    producers?: Array<{
      name: string;
      logo?: string;
      url?: string;
    }>;
    supporters?: Array<{
      name: string;
      logo?: string;
      url?: string;
    }>;
  };
  featureGrid?: {
    initialFeatures?: Array<{
      title: string;
      description: string;
      tooltip?: string;
    }>;
    additionalFeatures?: Array<{
      title: string;
      description: string;
      tooltip?: string;
    }>;
  };
  survey?: {
    title?: string;
    title_np?: string;
    thankYouTitle?: string;
    thankYouTitle_np?: string;
    thankYouText?: string;
    thankYouText_np?: string;
    questions?: Array<{
      id: string;
      text: string;
      text_np?: string;
      type: 'multiple-choice' | 'text' | 'phone';
      options?: string[];
      options_np?: string[];
    }>;
  };
  expand?: {
    cooperative: {
      id: string;
      name: string;
      image: string;
      slug: string;
    };
  };
}

// Update the ComponentConfig interface to match the response
interface ComponentConfig {
  id: string;
  collectionId: string;
  collectionName: string;
  product: string[];  // Always an array, could have one or more items
  show_hero: boolean;
  show_features: boolean;
  show_feature_grid: boolean;
  show_beekeeper: boolean;
  show_about: boolean;
  show_survey: boolean;
  show_faq: boolean;
  show_community: boolean;
  show_video: boolean;
  show_more_products: boolean;
  show_credit: boolean;
  show_customers: boolean;
  components_order: any; // Keep as any since it comes as "JSON" string
  created: string;
  updated: string;
}

// Add default config constant at the top of the file
const DEFAULT_COMPONENT_CONFIG: ComponentConfig = {
  id: 'default',
  collectionId: '',
  collectionName: '',
  product: [],
  show_hero: true,
  show_features: true,
  show_feature_grid: false,
  show_beekeeper: true,
  show_about: true,
  show_survey: true,
  show_faq: true,
  show_community: true,
  show_video: true,
  show_more_products: true,
  show_credit: true,
  show_customers: true,
  components_order: [
    'hero',
    'features',
    'beekeeper',
    'about',
    'survey',
    'community',
    'video',
    'more_products',
    'credit',
    'customers'
  ],
  created: '',
  updated: ''
};

// Make sure we're consistent with exports to avoid HMR issues
const ProductPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [componentConfig, setComponentConfig] = useState<ComponentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoader, setShowLoader] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    document.title = "Dailo Krishi Origin";
    let isSubscribed = true;
    
    const loadProductAndConfig = async () => {
      try {
        // First, get the product
        const record = await pb.collection('products').getFirstListItem<Product>(
          `slug="${slug}"`,
          {
            expand: 'cooperative'
          }
        );

        // Get translations
        try {
          const translation = await pb.collection('translations')
            .getFirstListItem(`collection_name = "products" && record_id = "${record.id}"`);
          
          // Combine product data with translations
          record.translations = translation;
        } catch (error) {
          // Silently handle missing translations
        }
        
        // Get component config
        const configResponse = await pb.collection('product_components').getList<ComponentConfig>(1, 1, {
          filter: `product ~ "${record.id}"`,
        });

        if (isSubscribed) {
          setProduct(record);
          if (configResponse.items.length > 0) {
            const config = configResponse.items[0];
            let parsedOrder;
            try {
              parsedOrder = typeof config.components_order === 'string' ? 
                JSON.parse(config.components_order) : 
                config.components_order;
            } catch (e) {
              parsedOrder = DEFAULT_COMPONENT_CONFIG.components_order;
            }
            
            setComponentConfig({
              ...config,
              components_order: parsedOrder
            });
          } else {
            setComponentConfig(DEFAULT_COMPONENT_CONFIG);
          }
          document.title = `${record.name} - Dailo Krishi Origin`;
          setInitialLoaded(true);
        }
      } catch (error: any) {
        if (isSubscribed) {
          setError(error?.message || "Failed to load product");
          setInitialLoaded(true);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      loadProductAndConfig();
    }

    return () => {
      isSubscribed = false;
      document.title = "Dailo Krishi Origin";
    };
  }, [slug]);

  // Update meta tags when product loads
  useEffect(() => {
    if (!product) return;

    // Update meta tags
    document.title = `${product.name} - Dailo Krishi Origin`;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', product.shortDescription || product.description || 'Explore authentic Nepali agricultural products');

    // Update OG tags
    const ogTags = {
      'og:title': `${product.name} - Dailo Krishi Origin`,
      'og:description': product.shortDescription || product.description,
      'og:image':pb.files.getURL(product, product.image),
      'og:url': window.location.href,
      'og:type': 'product'
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    });

    // Update Twitter Card tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': `${product.name} - Dailo Krishi Origin`,
      'twitter:description': product.shortDescription || product.description,
      'twitter:image': pb.files.getURL(product, product.image)
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    });

    // Cleanup function
    return () => {
      document.title = 'Dailo Krishi Origin';
      // Optionally remove meta tags on unmount if needed
    };
  }, [product]);

  if (loading && !initialLoaded) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-[rgba(0,63,40,1)]">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center gap-4">
        <div className="text-red-600 text-center px-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[rgba(0,63,40,1)] text-white rounded-lg"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!product || !componentConfig) return null;

  const renderComponent = (componentName: string) => {
    // Add debugging for the video component
    if (componentName === 'video') {
      console.log("Trying to render video section:", {
        show_video: componentConfig.show_video,
        productId: product.id,
        videoSectionTitle: product.videoSectionTitle,
        hasTranslations: !!product.translations
      });
    }
    
    switch (componentName) {
      case 'hero':
        return componentConfig.show_hero && <HeroSection product={product} />;
      case 'features':
        return componentConfig.show_features && <FeatureCard productId={product.id} />;
      case 'feature_grid':
        return componentConfig.show_feature_grid && (
          <FeatureGrid
            initialFeatures={product.featureGrid?.initialFeatures}
            additionalFeatures={product.featureGrid?.additionalFeatures}
          />
        );
      case 'beekeeper':
        return componentConfig.show_beekeeper && <BeekeeperSection cooperativeId={product.cooperative} />;
      case 'about':
        return componentConfig.show_about && <About product={product} />;
      case 'survey':
        return componentConfig.show_survey && (
          <SurveySection survey={product.survey} />
        );
      case 'faq':
        return componentConfig.show_faq && <FAQ productId={product.id} />;
      case 'community':
        return componentConfig.show_community && <JoinCommunity productId={product.id} />;
      case 'video':
        return componentConfig.show_video && <VideoPlayer 
          productId={product.id} 
          videoSectionTitle={product.videoSectionTitle || "Videos"} // Provide a fallback title
          productTranslations={product.translations} 
        />;
      case 'more_products':
        return componentConfig.show_more_products && <MoreProducts currentProductId={product.id} />;
      case 'credit':
        return componentConfig.show_credit && (
          <Credit
            producers={product.credit?.producers}
            supporters={product.credit?.supporters}
          />
        );
      case 'customers':
        return componentConfig.show_customers && (
          <Customers customers={product.customers} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showLoader && initialLoaded && (
        <ProductLoader 
          productName={product.name} 
          onLoadComplete={() => setShowLoader(false)} 
        />
      )}
      
      <div className={`bg-white flex flex-col min-h-screen ${showLoader ? 'hidden' : ''}`}>
        {/* <button 
          onClick={() => navigate('/')} 
          className="flex items-center gap-2 p-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button> */}

        {/* Modify how components are rendered to avoid the Fragment issue */}
        <div className="contents">
          {Array.isArray(componentConfig?.components_order) && 
            componentConfig.components_order.map((componentName: string, index) => {
              const Component = renderComponent(componentName);
              // Return the component directly with a key based on component name and index
              return Component ? (
                <div key={`${componentName}-${index}`} className="contents">
                  {Component}
                </div>
              ) : null;
            })}
        </div>
        <Footer />
      </div>
    </>
  );
};

// Use named export to maintain consistency
export { ProductPage };
// Also provide default export
export default ProductPage;
